/* Which market the visitor is in, and how the URL says so.
 *
 * URLs are /eg/… and /sa/…. The prefix is the market's `countries.slug`.
 *
 * MARKET_SLUGS below is a ROUTER MANIFEST, not a copy of the data. A static
 * site has to know its top-level path segments before it can ask the database
 * anything -- the router runs on the first paint, the fetch lands later -- so
 * the list of slugs that mean "this is a market" is compiled in. Everything
 * else about a market (its name, currency, format, timezone, features) comes
 * from the row and is never written here.
 *
 * scripts/verify-market-slugs.mjs FAILS THE GATE if this list and
 * `countries.slug` in the live database ever disagree, which is the part that
 * makes a compiled-in list safe: it cannot drift silently.
 */

export const MARKET_SLUGS = ["eg", "sa"] as const;
export type MarketSlug = (typeof MARKET_SLUGS)[number];

export const isMarketSlug = (s: string | undefined | null): s is MarketSlug =>
  !!s && (MARKET_SLUGS as readonly string[]).includes(s);

/** Split "/eg/search" into { slug: "eg", rest: "/search" }. */
export function splitMarketPath(pathname: string): { slug: MarketSlug | null; rest: string } {
  const m = pathname.match(/^\/([^/]+)(\/.*)?$/);
  if (m && isMarketSlug(m[1])) return { slug: m[1], rest: m[2] || "/" };
  return { slug: null, rest: pathname };
}

/** Put the market back on a path: ("/search", "eg") -> "/eg/search". */
export function withMarket(path: string, slug: string | null): string {
  if (!slug) return path;
  const clean = path.startsWith("/") ? path : `/${path}`;
  return clean === "/" ? `/${slug}/` : `/${slug}${clean}`;
}
